"use client"

import { useState, useEffect } from "react"
import "../styles/app.css"

import { Link, useNavigate } from "react-router-dom"
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const Calendar = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
)

const Sparkles = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"></path>
    <path d="M5 3v4"></path>
    <path d="M19 17v4"></path>
    <path d="M3 5h4"></path>
    <path d="M17 19h4"></path>
  </svg>
)

const mapGenderToValue = (gender) => {
  const genderMap = {
    'male': '1',
    'female': '2',
    'other': '3'
  };
  return genderMap[gender.toLowerCase()] || '3';
};

export default function Onboarding() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    age: "",
    gender: "",
  })

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Auth protection: check with backend if user is authenticated
  useEffect(() => {
    async function checkAuth() {
      try {
        const res = await fetch('/api/user-questionnaire-data/', {
          credentials: 'include',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (res.status === 200) {
          // User is authenticated, do nothing
        } else {
          navigate('/auth', { replace: true });
        }
      } catch {
        navigate('/auth', { replace: true });
      }
    }
    checkAuth();
  }, [navigate]);

  const handleAgeChange = (value) => {
    setFormData((prev) => ({ ...prev, age: value }))
  }

  const handleGenderChange = (value) => {
    setFormData((prev) => ({ ...prev, gender: value }))
  }

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      // Format data to match backend expectations
      const submitData = [
        { name: "q1", value: formData.age },
        { name: "q2", value: mapGenderToValue(formData.gender) }
      ];

      // Send data to backend
      const response = await fetch('/submitAns/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken'), // Django CSRF token
          'X-Requested-With': 'XMLHttpRequest', // Add this line for Django's is_ajax()
        },
        body: JSON.stringify({ data: JSON.stringify(submitData) }),
        credentials: 'include', // Ensure session cookie is sent
      });

      if (response.status === 403 || response.status === 401) {
        setError('Session expired. Please log in again.');
        setTimeout(() => navigate('/auth', { replace: true }), 1500);
        return;
      }

      const result = await response.json();

      if (result.Code === 111) {
        // Success - set flag for questionnaire modal and redirect to React home page
        localStorage.setItem('onboardingCompleted', 'true');
        navigate('/home');
      } else {
        setError(result.Message || 'Something went wrong');
      }
    } catch (err) {
      setError('Failed to submit data. Please try again.');
      console.error('Submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  }

  // Helper function to get CSRF token
  function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  const isFormComplete = formData.age && formData.gender

  return (
    <div className="app-container">
      <Header />
      <div className="onboarding-center-wrapper">
      <div className="main-content onboarding-content">
        <p className="onboarding-intro-text">Pickl knows what you like...</p>
        <div className="section-header">
          <h1 className="section-title">Tell us a little about yourself</h1>
          <p className="section-subtitle">We'll use this to get you started with personalized recommendations</p>
        </div>

        {error && (
          <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>
            {error}
          </div>
        )}

        <div className="form-section">
          <div className="question-group">
            <div className="question-header">
              <Calendar />
              <label className="question-label">How old are you?</label>
            </div>
            <select 
              value={formData.age} 
              onChange={(e) => handleAgeChange(e.target.value)} 
              className="select-input"
              disabled={isSubmitting}
            >
              <option value="">Select your age</option>
              {Array.from({ length: 87 }, (_, i) => i + 13).map((age) => (
                <option key={age} value={age.toString()}>
                  {age}
                </option>
              ))}
            </select>
          </div>

          <div className="question-group">
            <div className="question-header">
              <Sparkles />
              <label className="question-label">How do you identify?</label>
            </div>
            <div className="radio-grid gender-grid-inline">
              {["Male", "Female", "Other"].map((option) => (
                <label
                  key={option}
                  className={`radio-option ${formData.gender === option.toLowerCase() ? "selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="gender"
                    value={option.toLowerCase()}
                    checked={formData.gender === option.toLowerCase()}
                    onChange={(e) => handleGenderChange(e.target.value)}
                    className="radio-input"
                    disabled={isSubmitting}
                  />
                  {option}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button
            onClick={handleSubmit}
            disabled={!isFormComplete || isSubmitting}
            className={`btn btn-full ${!isFormComplete || isSubmitting ? "disabled" : "btn-primary"}`}
          >
            {isSubmitting ? "Submitting..." : "Get Started with Pickl"}
          </button>
        </div>
      </div>
      </div>

      <Footer />
    </div>
  )
} 